import React, { useState } from "react";
import { Progress } from "reactstrap";
import useCurrentPhotoStore from "stores/PhotoStore";
import useCurrentVehicleStore from "stores/InventoryStore";
import { getImageList, uploadImages } from "api/InventoryAPI";
import { useFromUser } from "stores/LocalStorageHelper";
import { failAlert } from "../../../../../utils/alertUtils";
import { createPhotosObj, getThumbnailURL } from "./helperFunctions";
import MultiImageUploader from "../../image/multi/MultiImageUploader";
import { useMultiImageData } from "../../image/multi/MultiImageContext";

/**
 * Component that allows users to upload images one at a time to prevent connection timeouts.
 */
const UploadPhotos = ({ newVehicle }) => {
	const MAX_UPLOADS = 100;

	// User store
	const dealerID = useFromUser("dealerID");
	const locationID = useFromUser("locationID");

	// Progress state for handling upload progress
	const [progress, setProgress] = useState(0);

	// Vehicle store
	const { vin, editThumbnail } = useCurrentVehicleStore();

	// Photo store
	const { gallery, editUploads, editGallery, editReorders } =
		useCurrentPhotoStore();

	// Multi image context
	const { imageSources, setImageSources, setFileUploads } = useMultiImageData();

	// Function to reset the upload state
	const resetUploadState = () => {
		setImageSources({});
		setFileUploads({});
	};

	// Function to validate image files
	const validateImage = async (blobUrl) => {
		try {
			const response = await fetch(blobUrl);
			const blob = await response.blob();
			const img = new Image();
			img.src = URL.createObjectURL(blob);
			return new Promise((resolve, reject) => {
				img.onload = () => resolve(blob);
				img.onerror = () => reject(new Error("Invalid image"));
			});
		} catch (error) {
			throw new Error("Invalid image");
		}
	};

	/**
	 * Uploads images in batches to the server with retry logic
	 */
	const upload = async () => {
		const imageFilesToArray = Object.entries(imageSources);

		if (isOverMaxUploads(imageFilesToArray.length)) {
			return;
		}

		const totalImages = imageFilesToArray.length;
		let uploadedCount = 0;
		const BATCH_SIZE = 5; // Upload 5 images at a time
		const MAX_RETRIES = 1;

		// Process images in batches
		for (let i = 0; i < imageFilesToArray.length; i += BATCH_SIZE) {
			const batch = imageFilesToArray.slice(i, i + BATCH_SIZE);
			let retryCount = 0;
			let batchSuccess = false;

			// Retry logic for each batch
			while (retryCount <= MAX_RETRIES && !batchSuccess) {
				try {
					console.log(
						`Uploading batch ${Math.floor(i / BATCH_SIZE) + 1} (${batch.length} images)${
							retryCount > 0 ? ` - Retry ${retryCount}` : ""
						}`
					);

					// Upload all images in the batch in parallel
					await Promise.all(
						batch.map(async ([filename, blobUrl], batchIndex) => {
							const blob = await validateImage(blobUrl);
							const singleImageFormData = new FormData();
							singleImageFormData.append(filename, blob, filename);

							await uploadImages(
								dealerID,
								locationID,
								vin,
								singleImageFormData,
								(progress) => {
									const currentImageProgress = i + batchIndex + progress / 100;
									setProgress(
										Math.round((currentImageProgress / totalImages) * 100)
									);
								}
							);

							console.log(`Successfully uploaded ${filename}`);
						})
					);

					uploadedCount += batch.length;
					console.log(`Batch complete. ${uploadedCount}/${totalImages} total uploaded.`);
					batchSuccess = true;
				} catch (error) {
					retryCount++;
					console.error(`Batch upload failed (attempt ${retryCount}):`, error);

					if (retryCount > MAX_RETRIES) {
						const failedFiles = batch.map(([filename]) => filename).join(", ");
						failAlert(
							`Failed to upload some images after ${MAX_RETRIES} retry: ${failedFiles}. Please try again.`
						);
						setProgress(0);
						resetUploadState();
						return;
					}

					// Wait a bit before retrying
					await new Promise((resolve) => setTimeout(resolve, 1000));
				}
			}
		}

		console.log(
			"All images uploaded successfully. Fetching updated image list."
		);
		const res = await getImageList(dealerID, locationID, vin);
		let imgArr = res.data.content;
		let photoObj = createPhotosObj(imgArr);

		editUploads({});
		editThumbnail(getThumbnailURL(imgArr));
		editGallery(photoObj);
		editReorders(photoObj);
		resetUploadState();
		setProgress(0);
		console.log("Upload process completed.");
	};

	// Verifies that user is not over max number of allowed uploads. Else continues
	// to upload photos
	const isOverMaxUploads = (uploadSize) => {
		if (gallery.length + uploadSize > MAX_UPLOADS) {
			failAlert(
				`A maximum of ${MAX_UPLOADS} images are allowed. You cannot upload any more images.`
			);
			return true;
		}
		return false;
	};

	return (
		<div>
			<div className="mt-3">
				<div className="d-flex justify-content-center align-items-center">
					{progress !== 0 && (
						<div className="text-center mb-5 w-75">
							<Progress striped max="100" value={progress}>
								<span className="progress-value">{progress}%</span>
							</Progress>
						</div>
					)}
				</div>
				<MultiImageUploader
					totalFiles={gallery.length}
					maxFiles={MAX_UPLOADS}
					handleUpload={upload}
					hideUploadButton={newVehicle}
				/>
			</div>
		</div>
	);
};

export default UploadPhotos;
